import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';

// Async thunks
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user, companies } = response.data;

      // Save to localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('username', user.firstName);
      localStorage.setItem('isSuperAdmin', user.isSuperAdmin);
      localStorage.setItem('user', JSON.stringify(user));

      return { token, user, companies };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al iniciar sesión');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post('/auth/logout');
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('username');
      localStorage.removeItem('isSuperAdmin');
      localStorage.removeItem('empresaActiva');
      localStorage.removeItem('user');
      localStorage.removeItem('userCompanies');
      return null;
    } catch (error) {
      // Even if API fails, clear local data
      localStorage.clear();
      return null;
    }
  }
);

export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/auth/profile');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al obtener perfil');
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async ({ currentPassword, newPassword }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/change-password', {
        currentPassword,
        newPassword
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error al cambiar contraseña');
    }
  }
);

// Initial state
const initialState = {
  user: (() => {
    // Cargar usuario desde localStorage si existe
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  })(),
  token: localStorage.getItem('token'),
  isAuthenticated: localStorage.getItem('isAuthenticated') === 'true',
  isSuperAdmin: localStorage.getItem('isSuperAdmin') === 'true',
  companies: (() => {
    // Cargar empresas desde localStorage si existen
    const savedCompanies = localStorage.getItem('userCompanies');
    return savedCompanies ? JSON.parse(savedCompanies) : [];
  })(),
  activeCompany: (() => {
    // Cargar empresa activa desde localStorage si existe
    const savedCompany = localStorage.getItem('empresaActiva');
    return savedCompany ? JSON.parse(savedCompany) : null;
  })(),
  loading: false,
  error: null,
};

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setActiveCompany: (state, action) => {
      state.activeCompany = action.payload;
      if (action.payload) {
        localStorage.setItem('empresaActiva', JSON.stringify(action.payload));
      } else {
        localStorage.removeItem('empresaActiva');
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.companies = action.payload.companies;
        state.isAuthenticated = true;
        state.isSuperAdmin = action.payload.user.isSuperAdmin;

        // Guardar empresas en localStorage para EmpresaContext
        localStorage.setItem('userCompanies', JSON.stringify(action.payload.companies));

        // Guardar preferencias de tema del usuario
        if (action.payload.user.themePrimaryColor) {
          localStorage.setItem('theme-primary-color', action.payload.user.themePrimaryColor);
        }
        if (action.payload.user.themeSecondaryColor) {
          localStorage.setItem('theme-secondary-color', action.payload.user.themeSecondaryColor);
        }

        // Set first company as active if user has companies
        if (action.payload.companies.length > 0) {
          state.activeCompany = action.payload.companies[0];
          localStorage.setItem('empresaActiva', JSON.stringify(action.payload.companies[0]));
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      });

    // Logout
    builder
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.companies = [];
        state.activeCompany = null;
        state.isAuthenticated = false;
        state.isSuperAdmin = false;
        state.error = null;
      });

    // Get Profile
    builder
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.companies = action.payload.companies || [];
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Change Password
    builder
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setActiveCompany } = authSlice.actions;
export default authSlice.reducer;